import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Navigate } from '~/utils/navigation';
import queryString from 'query-string';

import { setPostAuthLocation } from '~/components/authentication/ducks/actions';
import { authenticationSelector, selectPostAuthLocation } from '~/components/authentication/ducks/selectors';
import { selectBrand, selectDefaultProduct } from '~/components/brand/ducks/selectors';
import { isSmallerSelector } from '~/components/layout/mediaquery';
import { selectedLocale } from '~/components/locale/utils';
import useRouteMap from '~/components/router/use-route-map';
import useTranslations from '~/components/translation/use-translations';
import useConfiguration from '~/configuration/use-configuration';
import useContextualPathTransformer from '~/hooks/use-contextual-path-transformer';
import useRouterCallbacks from '~/hooks/use-router-callbacks';
import useBasePathname from '~/hooks/use-base-pathname';
import useExternalLogin from '~/components/guest-login/hooks/use-external-login';
import Home from './home';
import useRetainDefaultParams from '~/hooks/use-retain-default-params';

const textKeys = [
	'LANDING_PAGE_BONUS_TC_LINK_TEXT',
	'META_DESCRIPTION_START',
	'META_KEYWORDS_START',
	'MOBILE_DEPOSIT_DEPOSIT_BUTTON',
	'MOBILE_GLOBAL_MENU_PROMOTION_PAGE',
	'MOBILE_GLOBAL_MENU_PROMO_URL',
	'MOBILE_HELP_PAGE_SUBHEADER_ABOUT',
	'MOBILE_LANDINGPAGE_BONUS_TC_LINK',
	'MOBILE_LANDINGPAGE_BONUS_TC_TEXT',
	'MOBILE_LANDINGPAGE_BONUS_TC_URL',
	'MOBILE_LANDINGPAGE_LOGIN',
	'MOBILE_LANDINGPAGE_PROMOTEXT_1',
	'MOBILE_LANDINGPAGE_PROMOTEXT_2',
	'MOBILE_LANDINGPAGE_PROMOTEXT_3',
	'MOBILE_LANDINGPAGE_PROMOTIONS_LINK',
	'MOBILE_LANDINGPAGE_PROMOTIONS_LINK',
	'MOBILE_LANDINGPAGE_PROMOTIONS_URL',
	'MOBILE_LANDINGPAGE_SIGNUP',
	'MOBILE_LANDINGPAGE_UK_BONUS_LINK',
	'MOBILE_LANDINGPAGE_UK_BONUS_LINK_URL',
	'MOBILE_LOGIN_LOGIN_BUTTON',
	'MOBILE_LOGIN_REGISTRATION',
	'MOBILE_MAIN_MENU_CASINO',
	'MOBILE_MAIN_MENU_LIVEBETTING',
	'MOBILE_MAIN_MENU_LIVECASINO',
	'MOBILE_MAIN_MENU_SHOP',
	'MOBILE_MAIN_MENU_CAMPAIGN',
	'MOBILE_MAIN_MENU_WESPIN',
	'MOBILE_MAIN_MENU_SPORTS',
	'MOBILE_MAIN_MENU_HIGHLIGHT_GAMES',
	'MOBILE_MAIN_MENU_SUPPORT',
	'MOBILE_PAG_LOGIN',
	'MOBILE_TRUSTLY_DEPOSIT_LOGIN',
	'MOBILE_PLAY_NOW',
	'MOBILE_LANDINGPAGE_JACKPOTS_LINK',
	'MOBILE_LANDINGPAGE_JACKPOTS_LINK_TEXT',
	'MOBILE_VISIT_PRODUCT',
	'LANDING_PAGE_TERMS_CONDITIONS_TITLE',
	'MOBILE_PLAY_NOW',
	'SEO_JSONLD_HOMEPAGE'
];

const isForcedLanding = () => {
	const onMountQueryString = queryString.parse(window.location.search);
	return onMountQueryString.lp && onMountQueryString.lp === '1';
};

const HomeContainer = () => {
	const location = useLocation();
	const smaller = useSelector(isSmallerSelector);
	const defaultProduct = useSelector(selectDefaultProduct);
	const authenticated = useSelector(authenticationSelector);
	const { translations, isLoadingTranslations } = useTranslations(textKeys);
	const account = useSelector(state => state.account);
	const brand = useSelector(selectBrand);
	const currentLocale = useSelector(selectedLocale);
	const { transformPathAndPush, transformPathAndReplace } = useRouterCallbacks();
	const dispatch = useDispatch();
	const browserObject = useSelector(state => state.browser);
	const postAuthLocation = useSelector(selectPostAuthLocation);
	const supportLoaded = useSelector(state => state.support.supportLoaded);
	const configuration = useConfiguration();
	const { franchiseDefaultPage, newLandingPage } = brand;
	const transformPath = useContextualPathTransformer();
	const { redirectToExternalLogin } = useExternalLogin();
	const getLocationWithParams = useRetainDefaultParams();
	const basePathname = useBasePathname(defaultProduct);
	const routesMap = useRouteMap();

	const showProductPage = useMemo(() => {
		return (
			newLandingPage || !(isForcedLanding() || !franchiseDefaultPage || franchiseDefaultPage === 'landingpage')
		);
	}, [franchiseDefaultPage, newLandingPage]);

	// TODO: FIXME, this redirect should be moved server side
	const redirectPath = useMemo(() => {
		const isLastProduct = franchiseDefaultPage === 'last-product';
		const page = isLastProduct
			? basePathname
			: routesMap[franchiseDefaultPage] || routesMap[defaultProduct] || defaultProduct;
		const path = postAuthLocation && postAuthLocation.pathname ? postAuthLocation.pathname : postAuthLocation;
		if (path && (path === '/' || path.startsWith('/?'))) {
			const pathname = path.replace('/', page);
			const postLocation = typeof postAuthLocation === 'object' ? { ...postAuthLocation, pathname } : pathname;
			dispatch(setPostAuthLocation(postLocation));
		}
		return transformPath(getLocationWithParams(page), {
			preserveSidebarState: true
		});
	}, [
		franchiseDefaultPage,
		basePathname,
		routesMap,
		defaultProduct,
		postAuthLocation,
		transformPath,
		getLocationWithParams,
		dispatch
	]);

	if (isLoadingTranslations) {
		return <div className="spinner" />;
	}
	// TODO: FIXME, this redirect should be moved server side
	if (showProductPage) {
		return <Navigate to={redirectPath} replace="true" />;
	}
	return (
		<Home
			smaller={smaller}
			defaultProduct={defaultProduct}
			authenticated={authenticated}
			location={location}
			translations={translations}
			account={account}
			brand={brand}
			currentLocale={currentLocale}
			transformPathAndPush={transformPathAndPush}
			transformPathAndReplace={transformPathAndReplace}
			dispatch={dispatch}
			browserObject={browserObject}
			postAuthLocation={postAuthLocation}
			supportLoaded={supportLoaded}
			configuration={configuration}
			redirectPath={redirectPath}
			redirectToExternalLogin={redirectToExternalLogin}
		/>
	);
};

export default HomeContainer;
